import * as React from 'react';
import { FC, useState, useEffect, ChangeEventHandler, FocusEventHandler } from 'react';
import useInputErrorsDebouncer from 'app/useInputErrorsDebouncer';
import classnames from 'classnames';
import { isBlank } from './utils';
import { UpdateValidationState } from './useValidationState';

type Validation = 'required' | 'phoneWithoutCode' | 'phoneWithCode' | 'card' | 'html5';

export type FieldProps = {
  name: string;
  label: string;
  type?: string;
  hint?: string;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  length?: number;
  required?: boolean;
  pattern?: string;
  view?: 'phoneWithoutCode';
  validation?: Validation;
  updateValidationState: UpdateValidationState;
};

// Don't forget to synchronize changes with player validators

const PHONE_WITHOUT_CODE_VALIDATION_PATTERN = /^\d{10}$/;
const PHONE_WITHOUT_CODE_INPUT_PATTERN = /^\d{0,10}$/;
const PHONE_WITH_CODE_VALIDATION_PATTERN = /^\d{9,13}$/;
const PHONE_WITH_CODE_INPUT_PATTERN = /^\d{0,13}$/;

const canChange = (validation: Validation | undefined, value: string) => {
  if (validation === 'phoneWithoutCode') return PHONE_WITHOUT_CODE_INPUT_PATTERN.test(value);
  if (validation === 'phoneWithCode') return PHONE_WITH_CODE_INPUT_PATTERN.test(value);
  return true;
};

const useUpdateFieldValidationState = (props: FieldProps, value: string): [boolean, (v: string) => void] => {
  const { name, maxLength, minLength, length, required, pattern, updateValidationState, validation } = props;
  const [valid, setValid] = useState(true);

  const html5Validations = (v: string) =>
    (maxLength ? v.length <= maxLength : true) &&
    (minLength ? v.length >= minLength : true) &&
    (length ? v.length === length : true) &&
    (required ? !isBlank(v) : true) &&
    (pattern ? new RegExp(pattern).test(v) : true);

  const isValid = (v: string) => {
    switch (validation) {
      case 'required':
        return !isBlank(v);
      case 'phoneWithoutCode':
        return PHONE_WITHOUT_CODE_VALIDATION_PATTERN.test(v);
      case 'phoneWithCode':
        return PHONE_WITH_CODE_VALIDATION_PATTERN.test(v);
      case 'card':
        return /^\d{13,19}$/.test(v);
      case 'html5':
        return html5Validations(v);
      default:
        return true;
    }
  };

  const updateFieldValidationState = (v: string) => {
    if (!validation) return;

    const newValid = isValid(v);
    setValid(newValid);
    updateValidationState({ field: name, isValid: newValid });
  };

  useEffect(() => {
    updateFieldValidationState(value);
    return () => updateValidationState({ field: name, isValid: true });
  }, []);

  return [valid, updateFieldValidationState];
};

const Field: FC<FieldProps> = (props) => {
  const { name, label, type, hint, placeholder, view } = props;
  const [value, setValue] = useState('');
  const [tips, setTips] = useState(false);
  const [valid, updateFieldValidationState] = useUpdateFieldValidationState(props, value);
  const [canShowErrors, delayErrors] = useInputErrorsDebouncer();

  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const newValue = e.target.value;
    if (!canChange(props.validation, newValue)) return;

    setValue(newValue);
    updateFieldValidationState(newValue);
    delayErrors();
  };

  const onFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    setTips(true);
    e.target.placeholder = '';
  };

  const onBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    setTips(false);
    e.target.placeholder = placeholder || '';
  };

  const input = () => (
    <input
      type={type || 'text'}
      name={name}
      id={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete="off"
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );

  const phoneWithoutCode = () => (
    <div className="rs-flex rs-flex_collapse">
      <div className="rs-flex-item rs-small-1">
        <span className="prefix">+7</span>
      </div>
      <div className="rs-flex-item rs-small-11">{input()}</div>
    </div>
  );

  return (
    <div className="rs-row">
      <div
        className={classnames(
          { error: value && canShowErrors && !valid, tips_block: placeholder && tips },
          'rs-column-small-12 input'
        )}
      >
        <label htmlFor={name}>{`${label}:`}</label>
        {view === 'phoneWithoutCode' ? phoneWithoutCode() : input()}
        {placeholder && tips ? <p className="tips_text">{placeholder}</p> : null}
        {hint ? <p className="rs-text-size_small">{hint}</p> : null}
      </div>
    </div>
  );
};

export default Field;
